import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "bybit";
const WS_URL_SPOT = "wss://stream.bybit.com/v5/public/spot";
const WS_URL_LINEAR = "wss://stream.bybit.com/v5/public/linear"; // For USDT/USDC perpetuals
const PING_INTERVAL = 20000; // Bybit recommends ping every 20 seconds

/**
 * Check if pair symbol is a perpetual/futures pair
 * Perps format: BTC/USDT:USDT (contains ":" settlement token suffix)
 */
function isPerps(pairSymbol: string): boolean {
  return pairSymbol.includes(":");
}

/**
 * Get the settlement token from a perps pair symbol
 * BTC/USDT:USDT → USDT
 * BTC/USDC:USDC → USDC
 */
function getSettlementToken(pairSymbol: string): string | null {
  if (!isPerps(pairSymbol)) return null;
  return pairSymbol.split(":")[1] ?? null;
}

/**
 * Format pair symbol for Bybit WebSocket
 * Spot: BTC/USDT → BTCUSDT
 * Perps USDT: BTC/USDT:USDT → BTCUSDT
 * Perps USDC: BTC/USDC:USDC → BTCPERP (Bybit uses PERP suffix for USDC perps)
 */
function formatSymbol(pairSymbol: string): string {
  // For perps, handle different settlement tokens
  if (isPerps(pairSymbol)) {
    const basePair = pairSymbol.split(":")[0] ?? pairSymbol;
    const [base, quote] = basePair.split("/");
    const settlementToken = getSettlementToken(pairSymbol);

    // USDC perpetuals use BASE + "PERP" format (e.g., BTCPERP)
    if (settlementToken === "USDC" || quote === "USDC") {
      return `${base}PERP`;
    }

    // USDT perpetuals use normal format (e.g., BTCUSDT)
    return basePair.replace("/", "");
  }
  return pairSymbol.replace("/", "");
}

/**
 * Get the appropriate WebSocket URL based on market type
 */
function getWsUrl(pairSymbol: string): string {
  return isPerps(pairSymbol) ? WS_URL_LINEAR : WS_URL_SPOT;
}

// Orderbook state - maintained at module level to persist across re-renders
const orderbookState: {
  bids: Map<string, string>;
  asks: Map<string, string>;
  currentSymbol: string | null;
} = {
  bids: new Map(),
  asks: new Map(),
  currentSymbol: null,
};

/**
 * Hook for connecting to Bybit WebSocket and subscribing to orderbook updates
 * Bybit uses snapshot + delta pattern with Map-based state management
 */
export const useBybitOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const pingIntervalRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const startPing = useCallback((ws: WebSocket) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ op: "ping" }));
      }
    }, PING_INTERVAL);
  }, []);

  const stopPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = undefined;
    }
  }, []);

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    stopPing();

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, [stopPing]);

  const subscribeToOrderBook = useCallback((ws: WebSocket, symbol: string) => {
    const subscribeMessage = {
      op: "subscribe",
      args: [`orderbook.50.${symbol}`],
    };

    ws.send(JSON.stringify(subscribeMessage));
  }, []);

  /**
   * Convert module-level maps to sorted arrays and create snapshot
   */
  const createSnapshotFromState = useCallback((symbol: string): DirectOrderBookSnapshot => {
    // Convert bids map to sorted array (highest price first), limit to top 20
    const bids: [number, number][] = Array.from(orderbookState.bids.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => b[0] - a[0])
      .slice(0, 20);

    // Convert asks map to sorted array (lowest price first), limit to top 20
    const asks: [number, number][] = Array.from(orderbookState.asks.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .slice(0, 20);

    return {
      timestamp: Date.now(),
      exchangeId: EXCHANGE_ID,
      pairSymbol: symbol,
      bids,
      asks,
    };
  }, []);

  const connectWs = useCallback(() => {
    if (isConnecting.current || !enabled || !pairSymbol) return;

    disconnectWs();

    const symbol = formatSymbol(pairSymbol);

    // Reset orderbook state only if symbol changed
    if (orderbookState.currentSymbol !== pairSymbol) {
      orderbookState.bids = new Map();
      orderbookState.asks = new Map();
      orderbookState.currentSymbol = pairSymbol;
    }

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const wsUrl = getWsUrl(pairSymbol);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0;

        // Start ping
        startPing(ws);

        // Subscribe to orderbook
        subscribeToOrderBook(ws, symbol);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          // Handle pong response
          if (msg.op === "pong") return;

          // Handle subscription response
          if (msg.op === "subscribe" && msg.success) return;

          // Handle orderbook data
          if (msg.topic?.includes("orderbook") && msg.data) {
            const isSnapshot = msg.type === "snapshot";

            // On snapshot, reset state
            if (isSnapshot) {
              orderbookState.bids = new Map();
              orderbookState.asks = new Map();
            }

            // Apply bids: msg.data.b is [[price, size], ...]
            if (msg.data.b) {
              for (const [price, size] of msg.data.b) {
                if (parseFloat(size) === 0) {
                  orderbookState.bids.delete(price);
                } else {
                  orderbookState.bids.set(price, size);
                }
              }
            }

            // Apply asks: msg.data.a is [[price, size], ...]
            if (msg.data.a) {
              for (const [price, size] of msg.data.a) {
                if (parseFloat(size) === 0) {
                  orderbookState.asks.delete(price);
                } else {
                  orderbookState.asks.set(price, size);
                }
              }
            }

            // Create snapshot from updated state
            const snapshot = createSnapshotFromState(pairSymbol);
            handleOrderBookUpdate(snapshot);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);
        stopPing();

        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWs();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [
    pairSymbol,
    enabled,
    disconnectWs,
    handleOrderBookUpdate,
    subscribeToOrderBook,
    startPing,
    stopPing,
    createSnapshotFromState,
  ]);

  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWs();
      return;
    }

    connectWs();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWs();
      } else if (enabled && pairSymbol) {
        connectWs();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWs();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled, connectWs, disconnectWs]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
